import type { ChatRevertItem } from "schemas/generated/chatMessage";

import { useCallback } from "react";

import { skipToken } from "@tanstack/react-query";
import { useChatTurn } from "@v2/features/chat/hooks/useChatTurn";
import { useModifiersStoreActions } from "@v2/features/modifiers/store/ModifiersStoreContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  useEditorStoreActions,
  usePanelView,
  useViewingVersionId,
} from "@v2/store/editorStore";
import { chatRouter, chatUtils } from "@v2/trpc/routes/chat";
import {
  fileVersionRouter,
  fileVersionUtils,
} from "@v2/trpc/routes/fileVersion";
import { nextJSProjectRouter } from "@v2/trpc/routes/nextJSProject";
import { useSearchParams } from "react-router-dom";

export const useVersionHistory = ({
  fileVersionId,
}: {
  fileVersionId: string;
}) => {
  const { user } = useCurrentUser();
  const panelView = usePanelView();
  const viewingVersionId = useViewingVersionId();
  const logEvent = useLogAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const pageId = searchParams.get("pageId");
  const projectId = useCurrentProjectId();
  const { setViewingVersionId, setPanelView } = useEditorStoreActions();
  const modifierActions = useModifiersStoreActions();
  const { addChatTurn } = useChatTurn();

  const { mutateAsync: createChat } = chatRouter.create.useMutation();

  const { data: mostRecentFileVersionData } =
    fileVersionRouter.findByFileId.useQuery(
      pageId
        ? {
            fileId: pageId,
            limit: 1,
          }
        : skipToken,
    );

  const currentVersion = mostRecentFileVersionData?.fileVersions?.[0];
  const isCurrentVersion = Boolean(currentVersion?.id === fileVersionId);
  const isViewingVersion = Boolean(viewingVersionId === fileVersionId);

  const {
    mutateAsync: revertPageToVersion,
    isPending: isRevertingPageToVersion,
  } = nextJSProjectRouter.revertPageToVersion.useMutation({});

  const { mutate: addMessage } = chatRouter.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void chatUtils.get.invalidate({
          chatId,
        });
      }
      void fileVersionUtils.findByFileId.invalidate({
        fileId: pageId!,
      });
      void chatUtils.getPaginatedMessages.invalidate({
        chatId: chatId!,
      });
    },
  });

  const handleRevertPageToVersion = useCallback(async () => {
    if (projectId && user?.id) {
      const newVersion = await revertPageToVersion({
        projectId,
        fileVersionId,
        userId: user?.id,
      });

      logEvent("page.restore", {
        origin: panelView === "chat" ? "chat" : "version-history",
      });

      let chatIdToUse = chatId;

      if (!chatId) {
        const newChat = await createChat({
          projectId,
        });

        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("chatId", newChat.id);
          return newParams;
        });
        chatIdToUse = newChat.id;
      }

      if (chatIdToUse) {
        const revertItem: ChatRevertItem = {
          fileVersionId: newVersion.id,
          context: "User reverted to this version through the UI",
        };

        const { chatTurnId } = await addChatTurn({
          chatId: chatIdToUse,
        });

        addMessage({
          chatId: chatIdToUse,
          content: [
            {
              type: "text",
              contentType: "revert" as const,
              text: JSON.stringify(revertItem),
              isNewMessageGroup: true,
              fileId: pageId ?? undefined,
            },
          ],
          role: "user",
          chatTurnId,
        });
      }

      setViewingVersionId(null);
      modifierActions.setDisplayMode("select");
      setPanelView("chat");
    }
  }, [
    projectId,
    fileVersionId,
    user?.id,
    revertPageToVersion,
    chatId,
    addMessage,
    setViewingVersionId,
    modifierActions,
    setPanelView,
    addChatTurn,
    createChat,
    setSearchParams,
    pageId,
    logEvent,
    panelView,
  ]);

  const handlePreviewVersion = useCallback(() => {
    if (!projectId || !pageId || !fileVersionId) {
      return;
    }
    setViewingVersionId(fileVersionId);
    logEvent("page.preview", {
      origin: panelView === "chat" ? "chat" : "version-history",
    });
  }, [
    projectId,
    pageId,
    fileVersionId,
    setViewingVersionId,
    logEvent,
    panelView,
  ]);

  return {
    handleRevertPageToVersion,
    handlePreviewVersion,
    isRevertingPageToVersion,
    isViewingVersion,
    isCurrentVersion,
  };
};
